import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { Code2, GitBranch } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "~/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import type { ArtifactPreview } from "~/services/artifacts";
import { ArtifactCode } from "./ArtifactCode";
import { ArtifactLineage } from "./ArtifactLineage";
import { ArtifactToolbar } from "./ArtifactToolbar";

interface ArtifactShellProps {
  preview: ArtifactPreview;
  // The body — type-specific content (table, image, JSON, etc.). Per-type
  // renderers are responsible for nothing but this slot.
  children: ReactNode;
  // Optional larger / richer view rendered inside a modal when the user
  // clicks the toolbar's expand action. When omitted, expand is hidden.
  // Renderers use this for "full data" (dataframe) or "true size"
  // (figure, image) views.
  expandedContent?: ReactNode;
  className?: string;
}

// Artifact types that have meaningful upstream provenance. Value/text/blob
// are either terminal or have no notion of provenance in this system.
const LINEAGE_TYPES: ReadonlySet<ArtifactPreview["artifact_type"]> = new Set([
  "dataframe",
  "figure",
]);

const TYPE_LABELS: Record<string, string> = {
  dataframe: "Dataframe",
  figure: "Figure",
  image: "Image",
  value: "Value",
  text: "File",
  blob: "File",
};

type View = "data" | "code" | "lineage";

// Null when the shell has no expandedContent and therefore no modal to open.
const ArtifactExpandContext = createContext<(() => void) | null>(null);

export const useArtifactExpand = (): (() => void) | null => useContext(ArtifactExpandContext);

// Uniform card chrome around every artifact. Layout:
//
//   ┌──────────────────────────────────────────────────────┐
//   │ [Dataframe | Code | Lineage]          [↓] [↑] [⤢]    │  ← header
//   ├──────────────────────────────────────────────────────┤
//   │ <body>  -or-  <code panel>  -or-  <lineage panel>    │
//   │ summary (italic, no divider — visually grouped       │
//   │   with the body it describes)                        │
//   └──────────────────────────────────────────────────────┘
//
// The tab group on the left labels the artifact and lets the user swap
// between the artifact body, its source code, and its lineage. Tabs that
// don't apply (no code, non-derived type) are hidden. The right-side
// toolbar carries Download/Export/Expand actions only.
export const ArtifactShell = ({
  preview,
  children,
  expandedContent,
  className,
}: ArtifactShellProps) => {
  const [view, setView] = useState<View>("data");
  const [expandOpen, setExpandOpen] = useState(false);
  const requestExpand = useCallback(() => setExpandOpen(true), []);
  const title = preview.artifact_name ?? preview.filename;
  const hasSummary = Boolean(preview.summary);
  const hasCode = Boolean(preview.code);
  const hasLineage = LINEAGE_TYPES.has(preview.artifact_type);
  const typeLabel = TYPE_LABELS[preview.artifact_type] ?? preview.artifact_type;

  return (
    <div
      className={cn(
        "my-3 overflow-hidden rounded-lg border border-border bg-background/60",
        className
      )}
    >
      <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-3 py-1.5">
        <Tabs value={view} onValueChange={(v) => setView(v as View)}>
          <TabsList className="h-7 gap-0.5 bg-transparent p-0">
            <TabsTrigger
              value="data"
              className="h-7 rounded-md px-2 text-[11px] font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=active]:shadow-none"
            >
              {typeLabel}
            </TabsTrigger>
            {hasCode && (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* Span wrapper: TooltipTrigger's data-state would otherwise
                      clobber TabsTrigger's data-state via Radix's asChild prop
                      merge, breaking the active-tab style. */}
                  <span className="inline-flex">
                    <TabsTrigger
                      value="code"
                      aria-label="Code"
                      className="h-7 rounded-md px-2 text-muted-foreground hover:text-foreground focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=active]:shadow-none"
                    >
                      <Code2 className="size-3.5" />
                    </TabsTrigger>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Code</TooltipContent>
              </Tooltip>
            )}
            {hasLineage && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <TabsTrigger
                      value="lineage"
                      aria-label="Lineage"
                      className="h-7 rounded-md px-2 text-muted-foreground hover:text-foreground focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=active]:bg-muted data-[state=active]:text-foreground data-[state=active]:shadow-none"
                    >
                      <GitBranch className="size-3.5" />
                    </TabsTrigger>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Lineage</TooltipContent>
              </Tooltip>
            )}
          </TabsList>
        </Tabs>
        <div className="ml-auto">
          <ArtifactToolbar
            preview={preview}
            onExpand={expandedContent ? requestExpand : undefined}
          />
        </div>
      </div>
      <div className="px-3 py-4">
        {view === "code" && preview.code ? (
          <ArtifactCode code={preview.code} />
        ) : view === "lineage" ? (
          <ArtifactLineage artifactId={preview.artifact_id} />
        ) : (
          <ArtifactExpandContext.Provider value={expandedContent ? requestExpand : null}>
            {children}
          </ArtifactExpandContext.Provider>
        )}
      </div>
      {hasSummary && (
        <div className="px-3 pb-2 text-[11px] italic leading-snug text-muted-foreground">
          {preview.summary}
        </div>
      )}
      {expandedContent && (
        <Dialog open={expandOpen} onOpenChange={setExpandOpen}>
          <DialogContent className="flex max-h-[90vh] w-fit min-w-[480px] max-w-[95vw] flex-col gap-3 sm:max-w-[95vw]">
            <DialogTitle className="truncate pr-10 text-sm font-medium">
              {title ?? preview.artifact_type}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {preview.summary ?? `Expanded view of ${title ?? preview.artifact_type}`}
            </DialogDescription>
            <div className="min-h-0 flex-1 overflow-auto">{expandedContent}</div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
